import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ticketAPI } from '../../services/api';
import { Plus, LogOut, MessageSquare, Clock, Bell, CheckCircle, AlertCircle } from 'lucide-react';
import CreateTicketModal from '../CreateTicket/CreateTicketModal';
import io from 'socket.io-client';
import './UserDashboard.css';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:3001';

function UserDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [notification, setNotification] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const socketRef = useRef(null);

  useEffect(() => {
    loadTickets();
    
    // Initialize WebSocket connection
    const socket = io(SOCKET_URL);
    socketRef.current = socket;
    
    socket.emit('join_system', {
      userId: user.id,
      username: user.username,
      isAdmin: user.is_admin
    });
    
    // Listen for status changes
    socket.on('ticket_status_changed', (data) => {
      console.log('Ticket status changed:', data);
      const statusText = getStatusText(data.newStatus);
      const notif = {
        id: Date.now(),
        type: 'status',
        message: `Talep #${data.ticketId} durumu değişti: ${statusText}`,
        ticketId: data.ticketId,
        timestamp: new Date()
      };
      addNotification(notif);
      showNotification(notif.message);
      loadTickets(); // Refresh ticket list
    });
    
    // Listen for new admin messages
    socket.on('new_admin_message', (data) => {
      console.log('New message from admin:', data);
      const notif = {
        id: Date.now(),
        type: 'answered',
        message: `Talep #${data.ticketId} cevaplandı!`,
        ticketId: data.ticketId,
        timestamp: new Date()
      };
      addNotification(notif);
      showNotification(notif.message);
      loadTickets(); // Refresh to update message count
    });
    
    return () => {
      socket.disconnect();
    };
  }, [user]);

  // Close notifications dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showNotifications && !event.target.closest('.notifications-container')) {
        setShowNotifications(false);
      }
    };
  
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications]);

  const getStatusText = (status) => {
    const statusMap = {
      'pending': 'Beklemede',
      'open': 'Açık',
      'in_progress': 'İşlemde',
      'closed': 'Kapalı'
    };
    return statusMap[status] || status;
  };

  const loadTickets = async () => {
    try {
      const response = await ticketAPI.getTickets();
      if (response.success) {
        setTickets(response.tickets || []);
      }
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const playNotificationSound = () => {
    try {
      // Handle browser autoplay policy - resume context if suspended
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext();
      
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      // Silently fail - sound is optional
      console.log('Sound notification failed:', error);
    }
  };

  const showNotification = (message) => {
    playNotificationSound();
    setNotification(message);
    setTimeout(() => setNotification(null), 5000);
  };

  const addNotification = (notif) => {
    setNotifications(prev => [notif, ...prev].slice(0, 10)); // Keep last 10 notifications
  };

  const markNotificationAsRead = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const unreadCount = notifications.length;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleTicketClick = (ticketId, status) => {
    // Allow users to open closed tickets to view messages, but they won't be able to send messages
    navigate(`/ticket/${ticketId}`);
  };

  
  const handleCreateTicket = async (ticketData) => {
    try {
      const response = await ticketAPI.createTicket(ticketData);
      if (response.success) {
        setShowCreateModal(false);
        loadTickets();
        
        // Notify admins via WebSocket
        if (socketRef.current) {
          socketRef.current.emit('ticket_created', {
            ticketId: response.ticket_id,
            title: ticketData.title,
            category: ticketData.category,
            username: user.username
          });
        }
        
        // Navigate to the new ticket
        setTimeout(() => {
          navigate(`/ticket/${response.ticket_id}`);
        }, 300);
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      alert('Talep oluşturulamadı. Lütfen tekrar deneyin.');
    }
  };

  const getStatusClass = (status) => {
    const statusMap = {
      'pending': 'status-pending',
      'open': 'status-open',
      'in_progress': 'status-progress',
      'closed': 'status-closed'
    };
    return statusMap[status] || 'status-pending';
  };

  const getCategoryClass = (category) => {
    return `badge-${category.toLowerCase()}`;
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'şimdi';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} dakika önce`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} saat önce`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)} gün önce`;
    return date.toLocaleDateString('tr-TR');
  };

  return (
    <div className="dashboard-container">
      {/* Notification Banner */}
      {notification && (
        <div className="notification-banner">
          <Bell size={20} />
          <span>{notification}</span>
        </div>
      )}
      
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <h1>Hoş geldiniz {user?.username}</h1>
            <p>Son talepleriniz ve başvurularınız.</p>
          </div>
          <div className="header-right">
            <div className="notifications-container">
              <button 
                className="notification-btn" 
                onClick={() => setShowNotifications(!showNotifications)}
                title="Bildirimler"
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="notification-badge">{unreadCount}</span>
                )}
              </button>
              {showNotifications && (
                <div className="notifications-dropdown">
                  <div className="notifications-header">
                    <h3>Bildirimler</h3>
                    {notifications.length > 0 && (
                      <button 
                        className="clear-btn"
                        onClick={() => setNotifications([])}
                      >
                        Temizle
                      </button>
                    )}
                  </div>
                  {notifications.length === 0 ? (
                    <div className="no-notifications">
                      <Bell size={32} />
                      <p>Bildirim yok</p>
                    </div>
                  ) : (
                    <div className="notifications-list">
                      {notifications.map((notif) => (
                        <div 
                          key={notif.id} 
                          className="notification-item"
                          onClick={() => {
                            markNotificationAsRead(notif.id);
                            navigate(`/ticket/${notif.ticketId}`);
                            setShowNotifications(false);
                          }}
                        >
                          <div className="notification-icon">
                            {notif.type === 'answered' ? (
                              <CheckCircle size={18} color="#10b981" />
                            ) : (
                              <AlertCircle size={18} color="#3b82f6" />
                            )}
                          </div>
                          <div className="notification-content">
                            <p className="notification-message">{notif.message}</p>
                            <span className="notification-time">
                              {formatTimeAgo(notif.timestamp)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <button className="btn-secondary" onClick={handleLogout}>
              <LogOut size={18} />
              Çıkış
            </button>
            <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
              <Plus size={18} />
              Talep Oluştur
            </button>
          </div>
        </div>
      </header>

      {/* Tickets List */}
      <div className="tickets-container">
        {loading ? (
          <div className="loading">Talepler yüklen iyor...</div>
        ) : tickets.length === 0 ? (
          <div className="empty-state">
            <MessageSquare size={64} />
            <h3>Henüz talep yok</h3>
            <p>İlk talebinizi oluşturmak için "Talep Oluştur" butonuna tıklayın</p>
          </div>
        ) : (
          tickets.map((ticket) => (
            <div 
              key={ticket.id} 
              className="ticket-card"
              onClick={() => handleTicketClick(ticket.id, ticket.status)}
              style={{
                opacity: ticket.status === 'closed' ? 0.7 : 1,
                cursor: 'pointer',
                position: 'relative'
              }}
            >  


              {ticket.status === 'closed' && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: 'rgba(239, 68, 68, 0.95)',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  zIndex: 10,
                  pointerEvents: 'none'
                }}>
                  TALEP KAPATILDI
                </div>
              )}
              <div className="ticket-header">
            <div className="ticket-id-badge"># {ticket.id}</div>
                <div className="ticket-title-section">
                  <h3 className="ticket-title">{ticket.title}</h3>
                </div>
                <div className="ticket-time">
                  <Clock size={14} />
                  {formatTimeAgo(ticket.last_message_at || ticket.created_at)}
                </div>
              </div>
              
              {ticket.description && (
                <p className="ticket-description">{ticket.description}</p>
              )}
              
              <div className="ticket-meta">
                <span className={`ticket-badge ${getCategoryClass(ticket.category)}`}>
                  {ticket.category}
                </span>
                <span className={`ticket-status ${getStatusClass(ticket.status)}`}>
                  {getStatusText(ticket.status)}
                </span>
                {ticket.is_answered !== undefined && (
                  <span className={`ticket-answered-badge ${ticket.is_answered ? 'answered' : 'waiting'}`}>
                    {ticket.is_answered ? (
                      <>
                        <CheckCircle size={14} />
                        Cevaplandı
                      </>
                    ) : (
                      <>
                        <AlertCircle size={14} />
                        Yanıt Bekliyor
                      </>
                    )}
                  </span>
                )}
                <span className="ticket-badge">
                  <MessageSquare size={14} />
                  {ticket.message_count || 0} mesaj
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Ticket Modal */}
      {showCreateModal && (
        <CreateTicketModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateTicket}
        />
      )}
    </div>
  );
}

export default UserDashboard;
